/**
 * Pure JSON validators for the gamification configs.
 *
 * Each `validate*` function returns the strongly-typed config on success or
 * throws `ConfigValidationError` with a human-readable path-prefixed message.
 *
 * The validators check **structural invariants only** — required keys, types,
 * unique ids, and a small set of cross-references (e.g. `pair` symmetry).
 * Domain rules (e.g. "anchor maneuver must have ≥3 phases") are not enforced
 * here so that future configs remain easy to extend without code changes.
 */

import type {
  BadgeDef,
  BadgesConfig,
  DifficultyTierId,
  GamificationConfig,
  LevelsConfig,
  LocalizedLabels,
  ManeuverDef,
  ManeuverPhase,
  ManeuverStep,
  ManeuversConfig,
  RanksConfig,
  ScoringConfig,
} from "./gamification.types";

export class ConfigValidationError extends Error {
  constructor(message: string) {
    super(`[gamification config] ${message}`);
    this.name = "ConfigValidationError";
  }
}

const VALID_TIERS: readonly DifficultyTierId[] = [
  "calm",
  "moderate",
  "fresh",
  "gale",
];

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function ensureLabels(value: unknown, path: string): LocalizedLabels {
  if (
    !isObject(value) ||
    typeof value["en"] !== "string" ||
    typeof value["pl"] !== "string"
  ) {
    throw new ConfigValidationError(
      `${path}: expected { en: string, pl: string }`,
    );
  }
  return value as unknown as LocalizedLabels;
}

function ensureUniqueIds(
  items: readonly { readonly id: string }[],
  path: string,
): void {
  const seen = new Set<string>();
  for (const item of items) {
    if (seen.has(item.id)) {
      throw new ConfigValidationError(`${path}: duplicate id '${item.id}'`);
    }
    seen.add(item.id);
  }
}

// --- maneuvers ----------------------------------------------------------------

function validateStep(value: unknown, path: string): ManeuverStep {
  if (!isObject(value))
    throw new ConfigValidationError(`${path}: not an object`);
  const id = value["id"];
  const actor = value["actor"];
  const kind = value["kind"];
  if (typeof id !== "string")
    throw new ConfigValidationError(`${path}.id missing`);
  ensureLabels(value["labels"], `${path}.labels`);
  if (
    actor !== "skipper" &&
    actor !== "crew_bow" &&
    actor !== "crew_stern" &&
    actor !== "shore_party"
  ) {
    throw new ConfigValidationError(`${path}.actor invalid: ${String(actor)}`);
  }
  if (kind !== "check" && kind !== "command" && kind !== "action") {
    throw new ConfigValidationError(`${path}.kind invalid: ${String(kind)}`);
  }
  const autoCheck = value["autoCheck"];
  if (autoCheck !== null && !isObject(autoCheck)) {
    throw new ConfigValidationError(`${path}.autoCheck must be object or null`);
  }
  return value as unknown as ManeuverStep;
}

function validatePhase(value: unknown, path: string): ManeuverPhase {
  if (!isObject(value))
    throw new ConfigValidationError(`${path}: not an object`);
  if (typeof value["id"] !== "string") {
    throw new ConfigValidationError(`${path}.id missing`);
  }
  ensureLabels(value["labels"], `${path}.labels`);
  const steps = value["steps"];
  if (!Array.isArray(steps)) {
    throw new ConfigValidationError(`${path}.steps must be an array`);
  }
  steps.forEach((s, i) => validateStep(s, `${path}.steps[${i}]`));
  return value as unknown as ManeuverPhase;
}

function validateManeuver(value: unknown, path: string): ManeuverDef {
  if (!isObject(value))
    throw new ConfigValidationError(`${path}: not an object`);
  const id = value["id"];
  if (typeof id !== "string")
    throw new ConfigValidationError(`${path}.id missing`);
  ensureLabels(value["labels"], `${path}.labels`);
  if (typeof value["category"] !== "string") {
    throw new ConfigValidationError(`${path}.category missing`);
  }
  if (value["pair"] !== null && typeof value["pair"] !== "string") {
    throw new ConfigValidationError(`${path}.pair must be string or null`);
  }
  for (const numField of ["baseXp", "targetTimeSec", "timeLimitSec"]) {
    if (
      typeof value[numField] !== "number" ||
      !Number.isFinite(value[numField] as number)
    ) {
      throw new ConfigValidationError(
        `${path}.${numField} must be a finite number`,
      );
    }
  }
  if (!isObject(value["requirements"])) {
    throw new ConfigValidationError(`${path}.requirements must be an object`);
  }
  if (typeof value["targetKind"] !== "string") {
    throw new ConfigValidationError(`${path}.targetKind missing`);
  }
  const cs = value["commandSequence"];
  if (!Array.isArray(cs)) {
    throw new ConfigValidationError(`${path}.commandSequence must be an array`);
  }
  cs.forEach((p, i) => validatePhase(p, `${path}.commandSequence[${i}]`));
  const bcp = value["buoyContactPenalized"];
  if (bcp !== undefined && typeof bcp !== "boolean") {
    throw new ConfigValidationError(
      `${path}.buoyContactPenalized must be boolean when present`,
    );
  }
  const spawns = value["buoySpawns"];
  if (spawns !== undefined) {
    if (!Array.isArray(spawns)) {
      throw new ConfigValidationError(
        `${path}.buoySpawns must be an array when present`,
      );
    }
    spawns.forEach((s, i) => {
      const sp = `${path}.buoySpawns[${i}]`;
      if (!isObject(s)) throw new ConfigValidationError(`${sp}: not an object`);
      if (typeof s["kind"] !== "string")
        throw new ConfigValidationError(`${sp}.kind missing`);
      if (typeof s["id"] !== "string")
        throw new ConfigValidationError(`${sp}.id missing`);
      for (const f of ["forwardM", "rightM"]) {
        if (typeof s[f] !== "number" || !Number.isFinite(s[f] as number)) {
          throw new ConfigValidationError(`${sp}.${f} must be a finite number`);
        }
      }
      if (s["anchored"] !== undefined && typeof s["anchored"] !== "boolean") {
        throw new ConfigValidationError(`${sp}.anchored must be boolean`);
      }
    });
  }
  return value as unknown as ManeuverDef;
}

export function validateManeuvers(raw: unknown): ManeuversConfig {
  if (!isObject(raw))
    throw new ConfigValidationError("maneuvers: not an object");
  if (typeof raw["version"] !== "number") {
    throw new ConfigValidationError("maneuvers.version missing");
  }
  const arr = raw["maneuvers"];
  if (!Array.isArray(arr)) {
    throw new ConfigValidationError("maneuvers.maneuvers must be an array");
  }
  const list = arr.map((m, i) => validateManeuver(m, `maneuvers[${i}]`));
  ensureUniqueIds(list, "maneuvers");

  // Cross-check pair symmetry: if A.pair = B, then B.pair = A.
  const byId = new Map(list.map((m) => [m.id, m]));
  for (const m of list) {
    if (m.pair === null) continue;
    const partner = byId.get(m.pair);
    if (!partner) {
      throw new ConfigValidationError(
        `maneuvers[id=${m.id}].pair → unknown maneuver '${m.pair}'`,
      );
    }
    if (partner.pair !== m.id) {
      throw new ConfigValidationError(
        `maneuvers pair asymmetry: '${m.id}' ↔ '${m.pair}' (partner.pair='${partner.pair ?? "null"}')`,
      );
    }
  }
  return { version: raw["version"] as number, maneuvers: list };
}

// --- scoring ------------------------------------------------------------------

export function validateScoring(raw: unknown): ScoringConfig {
  if (!isObject(raw)) throw new ConfigValidationError("scoring: not an object");
  if (typeof raw["version"] !== "number") {
    throw new ConfigValidationError("scoring.version missing");
  }
  const tiers = raw["difficultyTiers"];
  if (!isObject(tiers)) {
    throw new ConfigValidationError(
      "scoring.difficultyTiers must be an object",
    );
  }
  for (const tierId of VALID_TIERS) {
    const tier = tiers[tierId];
    if (!isObject(tier)) {
      throw new ConfigValidationError(
        `scoring.difficultyTiers.${tierId} missing`,
      );
    }
    if (typeof tier["multiplier"] !== "number") {
      throw new ConfigValidationError(
        `scoring.difficultyTiers.${tierId}.multiplier missing`,
      );
    }
    for (const range of ["wind", "wave", "congestion"]) {
      const rangeValue = tier[range];
      if (
        !Array.isArray(rangeValue) ||
        rangeValue.length !== 2 ||
        typeof rangeValue[0] !== "number" ||
        typeof rangeValue[1] !== "number"
      ) {
        throw new ConfigValidationError(
          `scoring.difficultyTiers.${tierId}.${range} must be [number, number]`,
        );
      }
    }
  }
  if (
    !isObject(raw["performance"]) ||
    !isObject(raw["penalties"]) ||
    !isObject(raw["congestion"])
  ) {
    throw new ConfigValidationError(
      "scoring: missing one of { performance, penalties, congestion }",
    );
  }
  if (!Array.isArray(raw["timeBonus"])) {
    throw new ConfigValidationError("scoring.timeBonus must be an array");
  }
  return raw as unknown as ScoringConfig;
}

// --- ranks --------------------------------------------------------------------

export function validateRanks(raw: unknown): RanksConfig {
  if (!isObject(raw)) throw new ConfigValidationError("ranks: not an object");
  if (typeof raw["version"] !== "number") {
    throw new ConfigValidationError("ranks.version missing");
  }
  const arr = raw["ranks"];
  if (!Array.isArray(arr))
    throw new ConfigValidationError("ranks.ranks must be an array");
  arr.forEach((r, i) => {
    if (
      !isObject(r) ||
      typeof r["id"] !== "string" ||
      typeof r["xp"] !== "number"
    ) {
      throw new ConfigValidationError(`ranks[${i}] missing id/xp`);
    }
    ensureLabels(r["labels"], `ranks[${i}].labels`);
  });
  ensureUniqueIds(arr as { readonly id: string }[], "ranks");
  // Must be xp-ascending so binary search / lookup is O(log n).
  for (let i = 1; i < arr.length; i++) {
    if ((arr[i] as { xp: number }).xp <= (arr[i - 1] as { xp: number }).xp) {
      throw new ConfigValidationError(
        `ranks: xp must be strictly ascending (index ${i})`,
      );
    }
  }
  return raw as unknown as RanksConfig;
}

// --- badges -------------------------------------------------------------------

const VALID_PREDICATE_KINDS = new Set<string>([
  "firstSuccessInCategory",
  "metricBelow",
  "metricAtLeast",
  "streak",
  "completedAtTier",
  "countByCategory",
  "countByCategoryNoFlag",
  "flagAbsent",
  "timeRatioBelow",
  "successAfterFailures",
  "allManeuversInCategory",
]);

function validateBadge(value: unknown, path: string): BadgeDef {
  if (!isObject(value))
    throw new ConfigValidationError(`${path}: not an object`);
  if (typeof value["id"] !== "string")
    throw new ConfigValidationError(`${path}.id missing`);
  ensureLabels(value["labels"], `${path}.labels`);
  ensureLabels(value["description"], `${path}.description`);
  const pred = value["predicate"];
  if (!isObject(pred) || typeof pred["kind"] !== "string") {
    throw new ConfigValidationError(`${path}.predicate.kind missing`);
  }
  if (!VALID_PREDICATE_KINDS.has(pred["kind"] as string)) {
    throw new ConfigValidationError(
      `${path}.predicate.kind invalid: '${pred["kind"] as string}'`,
    );
  }
  return value as unknown as BadgeDef;
}

export function validateBadges(raw: unknown): BadgesConfig {
  if (!isObject(raw)) throw new ConfigValidationError("badges: not an object");
  if (typeof raw["version"] !== "number") {
    throw new ConfigValidationError("badges.version missing");
  }
  const arr = raw["badges"];
  if (!Array.isArray(arr))
    throw new ConfigValidationError("badges.badges must be an array");
  const list = arr.map((b, i) => validateBadge(b, `badges[${i}]`));
  ensureUniqueIds(list, "badges");
  return { version: raw["version"] as number, badges: list };
}

// --- levels -------------------------------------------------------------------

export function validateLevels(raw: unknown): LevelsConfig {
  if (!isObject(raw)) throw new ConfigValidationError("levels: not an object");
  if (typeof raw["version"] !== "number") {
    throw new ConfigValidationError("levels.version missing");
  }
  const arr = raw["levels"];
  if (!Array.isArray(arr))
    throw new ConfigValidationError("levels.levels must be an array");
  return raw as unknown as LevelsConfig;
}

// --- aggregate ----------------------------------------------------------------

export function validateGamificationConfig(input: {
  readonly maneuvers: unknown;
  readonly scoring: unknown;
  readonly ranks: unknown;
  readonly badges: unknown;
  readonly levels: unknown;
}): GamificationConfig {
  return {
    maneuvers: validateManeuvers(input.maneuvers),
    scoring: validateScoring(input.scoring),
    ranks: validateRanks(input.ranks),
    badges: validateBadges(input.badges),
    levels: validateLevels(input.levels),
  };
}
